import { useEffect, useMemo, useState } from "react";
import { useTagsWithRole } from "../../hooks/useRoles";
import { RoleTag } from "../../models/Role";
import { UserInformation } from "../../models/UserInformation";
import RoleAssignmentTree from "./RoleAssignmentTree";

interface RoleAssignmentEditorProps {
  tagCollectionId: string;
  onChange?: (updated: RoleTag[]) => void;
}

const RoleAssignmentEditor = ({
  tagCollectionId,
  onChange,
}: RoleAssignmentEditorProps) => {
  const { data: tagsWithRoles, isLoading: loadingTags } =
    useTagsWithRole(tagCollectionId);
  const [editedTags, setEditedTags] = useState<
    { tagCollectionId: string; tags: RoleTag[] } | undefined
  >(undefined);
  const tags = useMemo(
    () =>
      editedTags?.tagCollectionId === tagCollectionId
        ? editedTags.tags
        : (tagsWithRoles ?? []),
    [editedTags, tagCollectionId, tagsWithRoles]
  );

  useEffect(() => {
    onChange?.(tags);
  }, [tags, onChange]);

  const handlePrimaryUserChange = (
    tagId: string,
    userInfo: UserInformation | null
  ) => {
    setEditedTags({
      tagCollectionId,
      tags: tags.map((t) =>
        t.id === tagId ? { ...t, assignedPrimaryUser: userInfo } : t
      ),
    });
  };

  const handleSecondaryUserChange = (
    tagId: string,
    userInfo: UserInformation | null
  ) => {
    setEditedTags({
      tagCollectionId,
      tags: tags.map((t) =>
        t.id === tagId ? { ...t, assignedSecondaryUser: userInfo } : t
      ),
    });
  };

  const handleRoleTitleChange = (tagId: string, roleTitle: string) => {
    setEditedTags({
      tagCollectionId,
      tags: tags.map((t) => (t.id === tagId ? { ...t, roleTitle } : t)),
    });
  };

  return (
    <RoleAssignmentTree
      tagCollectionId={tagCollectionId}
      tags={tags}
      onPrimaryUserChange={handlePrimaryUserChange}
      onSecondaryUserChange={handleSecondaryUserChange}
      onRoleTitleChange={handleRoleTitleChange}
      loading={loadingTags}
    />
  );
};

export default RoleAssignmentEditor;
